import type { PriceDeal } from "@/types/api";
import { formatCurrency, formatWithKRW } from "@/lib/format";
import { useLang } from "@/i18n/LangContext";

interface PriceComparisonTableProps {
  priceDeals: PriceDeal[];
}

export default function PriceComparisonTable({ priceDeals }: PriceComparisonTableProps) {
  const { lang, t } = useLang();

  if (priceDeals.length === 0) return null;

  return (
    <div className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm p-4 sm:p-6 mb-6">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="w-8 h-8 rounded-lg bg-gray-900 dark:bg-card-border flex items-center justify-center">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
            <line x1="7" y1="7" x2="7.01" y2="7" />
          </svg>
        </div>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">{t.gameDetail.priceCompare}</h2>
      </div>
      <p className="text-xs sm:text-sm text-gray-500 dark:text-muted mb-4 ml-[42px]">
        {t.gameDetail.priceCompareDesc}
        <span className="text-gray-400 dark:text-muted ml-1">
          (via IsThereAnyDeal)
        </span>
      </p>
      <div className="space-y-2">
        {[...priceDeals]
          .sort((a, b) => a.price.amount - b.price.amount)
          .map((deal, idx) => {
            const isCheapest = idx === 0;
            return (
              <div
                key={`${deal.shop.id}-${idx}`}
                className={`flex items-center justify-between rounded-xl border p-3 sm:p-4 transition-colors ${
                  isCheapest
                    ? "bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30"
                    : "bg-inner-bg dark:bg-inner-bg border-inner-border dark:border-card-border hover:bg-hover-bg dark:hover:bg-hover-bg"
                }`}
              >
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center shrink-0 ${
                      isCheapest ? "bg-emerald-500" : "bg-gray-300 dark:bg-gray-600"
                    }`}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                      <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
                      <line x1="3" y1="6" x2="21" y2="6" />
                      <path d="M16 10a4 4 0 01-8 0" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <p className={`text-xs sm:text-sm font-semibold truncate ${isCheapest ? "text-emerald-800 dark:text-emerald-300" : "text-gray-800 dark:text-white"}`}>
                      {deal.shop.name}
                      {isCheapest && (
                        <span className="ml-1 sm:ml-2 text-[10px] sm:text-xs font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-500/20 px-1 sm:px-1.5 py-0.5 rounded">
                          {t.gameDetail.lowestPrice}
                        </span>
                      )}
                    </p>
                    {deal.cut > 0 && (
                      <p className="text-[10px] sm:text-xs text-gray-400 line-through">
                        {formatCurrency(deal.regular.amount, deal.regular.currency, lang)}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
                  {deal.cut > 0 && (
                    <span
                      className={`text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md sm:rounded-lg hidden sm:inline ${
                        isCheapest
                          ? "bg-emerald-500 text-white"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                      }`}
                    >
                      -{deal.cut}%
                    </span>
                  )}
                  <span className={`text-sm sm:text-base font-black tabular-nums ${isCheapest ? "text-emerald-700 dark:text-emerald-400" : "text-gray-900 dark:text-white"}`}>
                    {formatWithKRW(deal.price.amount, deal.price.currency, lang)}
                  </span>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}
